/**
 * OpenAPIUploader — Upload e gestão da spec OpenAPI
 *
 * Melhorias v2:
 * - Suporte a YAML e JSON
 * - Preview da spec com Swagger UI
 * - Validação detalhada (endpoints, schemas, info)
 * - Drag & drop
 * - Estatísticas da spec
 */

import React, { useState, useRef, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import { api, getErrorMessage } from '../../lib/api';
import { toast } from '../../utils/toast';
import { OpenAPIVisualBuilder } from './OpenAPIVisualBuilder';
import {
  FileText,
  Tag,
  Link2,
  Database,
  Code,
  LayoutGrid,
  Edit3,
  Eye,
  FolderOpen,
  AlertTriangle,
  CheckCircle2,
  Save,
  Info,
} from 'lucide-react';

interface OpenAPIUploaderProps {
  productId: string;
  currentSpec?: string;
  onUpdate: () => void;
  endpoints: import('../../types').ProductEndpoint[];
}

interface SpecStats {
  title: string;
  version: string;
  endpointCount: number;
  schemaCount: number;
  tagCount: number;
  hasServers: boolean;
  format: 'json' | 'yaml';
}

interface ParsedEndpoint {
  path: string;
  method: string;
  summary?: string;
  description?: string;
  operationId?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────

async function parseSpec(content: string): Promise<{ parsed: any; format: 'json' | 'yaml' }> {
  const trimmed = content.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    return { parsed: JSON.parse(trimmed), format: 'json' };
  }
  // YAML — dynamic import to avoid bundle bloat
  const yaml = await import('js-yaml');
  return { parsed: yaml.load(trimmed), format: 'yaml' };
}

function extractStats(parsed: any, format: 'json' | 'yaml'): SpecStats {
  const paths = parsed?.paths || {};
  const methods = ['get', 'post', 'put', 'patch', 'delete', 'options', 'head'];
  let endpointCount = 0;
  let inlineSchemaCount = 0;
  Object.values(paths).forEach((pathObj: any) => {
    methods.forEach((m) => {
      const op = pathObj?.[m];
      if (op) {
        endpointCount++;
        if (op.requestBody?.content) {
          Object.values(op.requestBody.content).forEach((media: any) => {
            if (media?.schema && !media.schema.$ref) inlineSchemaCount++;
          });
        }
        if (op.responses) {
          Object.values(op.responses).forEach((resp: any) => {
            if (resp?.content) {
              Object.values(resp.content).forEach((media: any) => {
                if (media?.schema && !media.schema.$ref) inlineSchemaCount++;
              });
            }
          });
        }
      }
    });
  });

  const componentSchemaCount = Object.keys(parsed?.components?.schemas || {}).length;

  return {
    title: parsed?.info?.title || 'Sem título',
    version: parsed?.info?.version || '—',
    endpointCount,
    schemaCount: componentSchemaCount + inlineSchemaCount,
    tagCount: (parsed?.tags || []).length,
    hasServers: (parsed?.servers || []).length > 0,
    format,
  };
}

function extractEndpoints(parsed: any): ParsedEndpoint[] {
  const paths = parsed?.paths || {};
  const methods = ['get', 'post', 'put', 'patch', 'delete'];
  const endpoints: ParsedEndpoint[] = [];
  Object.entries(paths).forEach(([path, pathObj]: [string, any]) => {
    methods.forEach((method) => {
      if (pathObj[method]) {
        endpoints.push({
          path,
          method,
          summary: pathObj[method].summary,
          description: pathObj[method].description,
          operationId: pathObj[method].operationId,
        });
      }
    });
  });
  return endpoints;
}

// ── Componente ────────────────────────────────────────────────────────────

export const OpenAPIUploader: React.FC<OpenAPIUploaderProps> = ({
  productId,
  currentSpec,
  onUpdate,
  endpoints,
}) => {
  const [specContent, setSpecContent] = useState(currentSpec || '');
  const [parsedSpec, setParsedSpec] = useState<any>(null);
  const [specStats, setSpecStats] = useState<SpecStats | null>(null);
  const [parsedEndpoints, setParsedEndpoints] = useState<ParsedEndpoint[]>([]);
  const [selectedEndpoints, setSelectedEndpoints] = useState<Set<string>>(new Set());
  const [validationError, setValidationError] = useState('');
  const [activeView, setActiveView] = useState<'visual' | 'code' | 'preview'>('visual');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleVisualChange = (updatedSpec: any) => {
    setParsedSpec(updatedSpec);
    setSpecStats(extractStats(updatedSpec, 'yaml')); // default visual to yaml output internally for stats

    // Convert back to YAML string for the editor and saving
    import('js-yaml')
      .then((yaml) => {
        setSpecContent(yaml.dump(updatedSpec));
      })
      .catch(() => {});
  };

  const handleStringChange = (updatedString: string) => {
    setSpecContent(updatedString);
    parseSpec(updatedString)
      .then(({ parsed, format }) => {
        setParsedSpec(parsed);
        setSpecStats(extractStats(parsed, format));
      })
      .catch(() => {});
  };

  // Parse current spec on mount for preview & endpoint detection
  React.useEffect(() => {
    if (currentSpec) {
      setSpecContent(currentSpec);
      parseSpec(currentSpec)
        .then(({ parsed, format }) => {
          setParsedSpec(parsed);
          setSpecStats(extractStats(parsed, format));
          const detected = extractEndpoints(parsed);
          // Filter out endpoints that already exist in product_endpoints
          const existingKeys = new Set(
            (endpoints || []).map(
              (ep) => `${String(ep.public_method).toLowerCase()}:${ep.public_path}`
            )
          );
          const missing = detected.filter(
            (ep) => !existingKeys.has(`${ep.method.toLowerCase()}:${ep.path}`)
          );
          setParsedEndpoints(missing.length > 0 ? missing : detected);
          setSelectedEndpoints(
            new Set((missing.length > 0 ? missing : []).map((_, i) => i.toString()))
          );
        })
        .catch(() => {});
    }
  }, [currentSpec, endpoints]);

  // ── Mutations ────────────────────────────────────────────────────────────

  const uploadMutation = useMutation({
    mutationFn: async (spec: string) => {
      await api.put(`/portal/admin/products/${productId}`, { openapi_spec: spec });
    },
    onSuccess: () => {
      onUpdate();
      toast.success('Especificação OpenAPI guardada com sucesso!');
    },
    onError: (error) => {
      toast.error(`Erro ao guardar: ${getErrorMessage(error)}`);
    },
  });

  const createEndpointsMutation = useMutation({
    mutationFn: async () => {
      const toCreate = parsedEndpoints.filter((_, i) => selectedEndpoints.has(i.toString()));
      await api.post(`/portal/admin/products/${productId}/endpoints/bulk`, {
        endpoints: toCreate.map((ep) => ({
          name: ep.summary || ep.operationId || `${ep.method.toUpperCase()} ${ep.path}`,
          description: ep.description,
          public_method: ep.method.toUpperCase(),
          public_path: ep.path,
          external_method: ep.method.toUpperCase(),
          external_path: ep.path,
          enabled: true,
        })),
      });
    },
    onSuccess: () => {
      onUpdate();
      toast.success(`${selectedEndpoints.size} endpoint(s) criado(s) com sucesso!`);
      setParsedEndpoints([]);
      setSelectedEndpoints(new Set());
    },
    onError: (error) => {
      toast.error(`Erro ao criar endpoints: ${getErrorMessage(error)}`);
    },
  });

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleFileContent = useCallback(async (content: string) => {
    setSpecContent(content);
    setValidationError('');
    setParsedEndpoints([]);
    try {
      const { parsed, format } = await parseSpec(content);
      setParsedSpec(parsed);
      setSpecStats(extractStats(parsed, format));
      const endpoints = extractEndpoints(parsed);
      setParsedEndpoints(endpoints);
      setSelectedEndpoints(new Set(endpoints.map((_, i) => i.toString())));
    } catch (err: any) {
      setValidationError(`Erro ao analisar a spec: ${err.message}`);
      setParsedSpec(null);
      setSpecStats(null);
    }
  }, []);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => handleFileContent(ev.target?.result as string);
    reader.readAsText(file);
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => handleFileContent(ev.target?.result as string);
      reader.readAsText(file);
    },
    [handleFileContent]
  );

  const handleTextChange = (value: string) => {
    setSpecContent(value);
    if (value.trim()) {
      handleFileContent(value);
    } else {
      setParsedSpec(null);
      setSpecStats(null);
      setValidationError('');
    }
  };

  const handleSave = () => {
    if (!specContent.trim()) {
      toast.error('A especificação não pode estar vazia');
      return;
    }
    uploadMutation.mutate(specContent);
  };

  const toggleEndpoint = (index: number) => {
    const key = index.toString();
    const updated = new Set(selectedEndpoints);
    updated.has(key) ? updated.delete(key) : updated.add(key);
    setSelectedEndpoints(updated);
  };

  const METHOD_COLORS: Record<string, string> = {
    get: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400',
    post: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
    put: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
    patch: 'bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-400',
    delete: 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400',
  };

  return (
    <div className="space-y-6">
      {/* Stats da spec actual */}
      {specStats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            {
              label: 'Título',
              value: specStats.title,
              icon: <FileText className="w-5 h-5 text-primary-500 mx-auto" />,
            },
            {
              label: 'Versão',
              value: specStats.version,
              icon: <Tag className="w-5 h-5 text-indigo-500 mx-auto" />,
            },
            {
              label: 'Endpoints',
              value: specStats.endpointCount,
              icon: <Link2 className="w-5 h-5 text-emerald-500 mx-auto" />,
            },
            {
              label: 'Schemas',
              value: specStats.schemaCount,
              icon: <Database className="w-5 h-5 text-amber-500 mx-auto" />,
            },
            {
              label: 'Formato',
              value: specStats.format.toUpperCase(),
              icon: <Code className="w-5 h-5 text-purple-500 mx-auto" />,
            },
          ].map((stat) => (
            <div key={stat.label} className="card p-3 text-center">
              <div className="mb-1.5">{stat.icon}</div>
              <div
                className="text-sm font-semibold text-slate-900 dark:text-white truncate"
                title={String(stat.value)}
              >
                {stat.value}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">{stat.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs: Editor / Preview */}
      <div className="card p-0 overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pr-4 flex-wrap gap-2">
          <div className="flex">
            <button
              onClick={() => setActiveView('visual')}
              className={`px-4 py-3 text-sm font-semibold transition-colors inline-flex items-center gap-2 ${
                activeView === 'visual'
                  ? 'border-b-2 border-primary-500 text-primary-600 dark:text-primary-400 bg-white dark:bg-slate-900'
                  : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 bg-slate-50 dark:bg-slate-800/60'
              }`}
            >
              <LayoutGrid className="w-4 h-4" />
              <span>Construtor Visual</span>
            </button>
            <button
              onClick={() => setActiveView('code')}
              className={`px-4 py-3 text-sm font-semibold transition-colors inline-flex items-center gap-2 ${
                activeView === 'code'
                  ? 'border-b-2 border-primary-500 text-primary-600 dark:text-primary-400 bg-white dark:bg-slate-900'
                  : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 bg-slate-50 dark:bg-slate-800/60'
              }`}
            >
              <Edit3 className="w-4 h-4" />
              <span>Código (YAML / JSON)</span>
            </button>
            <button
              onClick={() => setActiveView('preview')}
              disabled={!parsedSpec}
              className={`px-4 py-3 text-sm font-semibold transition-colors disabled:opacity-40 inline-flex items-center gap-2 ${
                activeView === 'preview'
                  ? 'border-b-2 border-primary-500 text-primary-600 dark:text-primary-400 bg-white dark:bg-slate-900'
                  : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 bg-slate-50 dark:bg-slate-800/60'
              }`}
            >
              <Eye className="w-4 h-4" />
              <span>Preview Swagger UI</span>
            </button>
          </div>
          <button
            onClick={handleSave}
            disabled={!specContent || uploadMutation.isPending || !!validationError}
            className="btn-primary text-xs py-1.5 px-3 inline-flex items-center gap-1.5"
          >
            <Save className="w-3.5 h-3.5" />
            <span>{uploadMutation.isPending ? 'A guardar...' : 'Guardar Especificação'}</span>
          </button>
        </div>

        {activeView === 'visual' && (
          <div className="p-4 bg-slate-50 dark:bg-slate-900/50">
            <OpenAPIVisualBuilder
              parsedSpec={parsedSpec}
              specString={specContent}
              endpoints={endpoints}
              onChange={handleVisualChange}
              onStringChange={handleStringChange}
            />
            {/* Visual mode saves via the same mechanism, we keep the save button at the bottom */}
          </div>
        )}

        {activeView === 'code' && (
          <div className="p-4 space-y-4">
            {/* Drag & Drop zone */}
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                isDragging
                  ? 'border-primary-400 bg-primary-50 dark:bg-primary-950/30'
                  : 'border-slate-300 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800/50'
              }`}
            >
              <FolderOpen className="w-8 h-8 text-primary-500 mx-auto mb-2" />
              <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Arraste um ficheiro aqui ou clique para seleccionar
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Suporta JSON e YAML (.json, .yaml, .yml)
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,.yaml,.yml"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>

            {/* Editor de texto */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="label text-xs">Especificação OpenAPI (JSON ou YAML)</label>
                {specContent && (
                  <span className="text-xs text-slate-400">
                    {specContent.length.toLocaleString()} caracteres
                  </span>
                )}
              </div>
              <textarea
                className="textarea h-72 font-mono text-xs"
                value={specContent}
                onChange={(e) => handleTextChange(e.target.value)}
                placeholder={
                  'openapi: "3.0.3"\ninfo:\n  title: Minha API\n  version: "1.0.0"\npaths:\n  /exemplo:\n    get:\n      summary: Exemplo\n      responses:\n        "200":\n          description: OK'
                }
                spellCheck={false}
              />
            </div>

            {/* Erro de validação */}
            {validationError && (
              <div className="rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/50 p-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-600 dark:text-rose-400 shrink-0" />
                <p className="text-sm text-rose-800 dark:text-rose-300">{validationError}</p>
              </div>
            )}

            {/* Aviso de validação OK */}
            {specStats && !validationError && (
              <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/50 p-3 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                <p className="text-sm text-emerald-800 dark:text-emerald-300">
                  Spec válida — {specStats.endpointCount} endpoint(s), {specStats.schemaCount}{' '}
                  schema(s)
                </p>
              </div>
            )}

            {/* Acções */}
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={!specContent || uploadMutation.isPending || !!validationError}
                className="btn-primary inline-flex items-center gap-1.5"
              >
                <Save className="w-4 h-4" />
                <span>{uploadMutation.isPending ? 'A guardar...' : 'Guardar Especificação'}</span>
              </button>
              {parsedSpec && (
                <button
                  onClick={() => setActiveView('preview')}
                  className="btn-secondary inline-flex items-center gap-1.5"
                >
                  <Eye className="w-4 h-4" />
                  <span>Ver Preview</span>
                </button>
              )}
            </div>
          </div>
        )}

        {activeView === 'preview' && parsedSpec && (
          <div className="p-0">
            <div className="bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-800/50 px-4 py-2 flex items-center gap-2">
              <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
              <p className="text-xs text-amber-800 dark:text-amber-300">
                Preview da documentação tal como será vista pelos clientes no Developer Portal
              </p>
            </div>
            <div
              className="p-4 bg-white dark:bg-slate-900"
              style={{ maxHeight: '650px', overflowY: 'auto' }}
            >
              <SwaggerUI
                spec={parsedSpec}
                docExpansion="list"
                defaultModelsExpandDepth={2}
                defaultModelExpandDepth={2}
                defaultModelRendering="example"
                displayRequestDuration={true}
                tryItOutEnabled={false}
                supportedSubmitMethods={[]}
              />
            </div>
          </div>
        )}
      </div>

      {/* Endpoints detectados — opção de criar */}
      {parsedEndpoints.length > 0 && (
        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                Endpoints Detectados ({parsedEndpoints.length})
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                Seleccione os endpoints a criar automaticamente na BD
              </p>
            </div>
            <div className="flex gap-2 items-center">
              <button
                onClick={() =>
                  setSelectedEndpoints(new Set(parsedEndpoints.map((_, i) => i.toString())))
                }
                className="text-xs text-primary-600 dark:text-primary-400 hover:underline font-medium"
              >
                Seleccionar todos
              </button>
              <button
                onClick={() => setSelectedEndpoints(new Set())}
                className="text-xs text-slate-500 dark:text-slate-400 hover:underline font-medium"
              >
                Limpar
              </button>
              <button
                onClick={() => createEndpointsMutation.mutate()}
                disabled={selectedEndpoints.size === 0 || createEndpointsMutation.isPending}
                className="btn-success text-sm"
              >
                {createEndpointsMutation.isPending
                  ? 'A criar...'
                  : `Criar ${selectedEndpoints.size} Endpoint(s)`}
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            {parsedEndpoints.map((endpoint, index) => (
              <div
                key={index}
                onClick={() => toggleEndpoint(index)}
                className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                  selectedEndpoints.has(index.toString())
                    ? 'border-primary-300 bg-primary-50 dark:border-primary-700 dark:bg-primary-950/30'
                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 bg-white dark:bg-slate-800/60'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedEndpoints.has(index.toString())}
                  onChange={() => toggleEndpoint(index)}
                  onClick={(e) => e.stopPropagation()}
                  className="w-4 h-4 text-primary-600 rounded"
                />
                <span
                  className={`text-xs font-bold px-2 py-0.5 rounded uppercase ${METHOD_COLORS[endpoint.method] || 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'}`}
                >
                  {endpoint.method}
                </span>
                <code className="text-sm font-mono text-slate-800 dark:text-slate-200 flex-1">
                  {endpoint.path}
                </code>
                {endpoint.summary && (
                  <span className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-xs">
                    {endpoint.summary}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Spec actual */}
      {currentSpec && !specContent && (
        <div className="card bg-slate-50 dark:bg-slate-800/50">
          <h4 className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
            Especificação Actual
          </h4>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
            Já existe uma especificação para este produto. Carregar uma nova irá substituí-la.
          </p>
          <button
            onClick={() => {
              setSpecContent(currentSpec);
              void handleFileContent(currentSpec);
            }}
            className="btn-secondary text-sm"
          >
            Editar Especificação Actual
          </button>
        </div>
      )}
    </div>
  );
};
